import { useEffect, useMemo, useRef } from 'react';
import { useIsMutating, useQuery, useQueryClient } from '@tanstack/react-query';
import { getAssignmentFeedbackSummaries } from '../service';
import { shouldRetryQuery } from '../utils/queryRetry';
import { STATUS_READ_TIMEOUT_MS } from './useRunStatusQuery';
import {
  assignmentFeedbackRequestKey,
  assignmentFeedbackRunKey,
  useAssignmentSubmissionsStatus,
} from './useAssignmentFeedbackRun';
import {
  isRunActive,
  runProgress,
} from '../components/BulkUpload/utils/feedbackRun';

// The task list's feedback progress bar, one per card, without one request per card.
//
// Every card used to read GET /assignments/{id}/submissions/status for itself: ~180
// requests on one staging teacher's Tasks page, all to learn that nothing was running.
// Now each card asks a shared loader for its summary, the loader sends every id asked
// for in the same few milliseconds as one POST /assignments/submissionStatusSummaries,
// and only a card whose run is moving falls back to the per-id read, for its poll.

/** The server's cap on ids per summaries read. */
export const SUMMARY_BATCH_SIZE = 100;
/** Long enough to catch every card that mounts in one commit, short enough not to show. */
export const SUMMARY_BATCH_WAIT_MS = 10;
/** After a status read fails for good: wait this long, then try again, at most this often. */
export const STATUS_RECOVERY_DELAY_MS = 15_000;
export const STATUS_RECOVERY_ATTEMPTS = 4;

export const assignmentFeedbackSummaryKey = (assignmentId) => [
  'assignmentFeedbackSummary',
  assignmentId,
];

/**
 * Collects single-id reads into batched ones: every `load(id)` made within `waitMs`
 * of the first is answered by one `readMany(ids)` per `maxBatch` ids, which resolves
 * to `{ [id]: value }`. An id missing from the answer resolves to null; a failed read
 * rejects every id it carried.
 */
export const createBatchLoader = (
  readMany,
  { maxBatch = SUMMARY_BATCH_SIZE, waitMs = SUMMARY_BATCH_WAIT_MS } = {}
) => {
  let waiting = new Map();
  let timer = null;

  const settle = (ids, waiters, byId) =>
    ids.forEach((id) =>
      waiters.get(id).forEach(({ resolve }) => resolve(byId?.[id] ?? null))
    );
  const fail = (ids, waiters, error) =>
    ids.forEach((id) => waiters.get(id).forEach(({ reject }) => reject(error)));

  const flush = () => {
    const waiters = waiting;
    waiting = new Map();
    timer = null;
    const ids = [...waiters.keys()];
    for (let start = 0; start < ids.length; start += maxBatch) {
      const chunk = ids.slice(start, start + maxBatch);
      Promise.resolve()
        .then(() => readMany(chunk))
        .then(
          (byId) => settle(chunk, waiters, byId),
          (error) => fail(chunk, waiters, error)
        );
    }
  };

  return (id) =>
    new Promise((resolve, reject) => {
      waiting.set(id, [...(waiting.get(id) || []), { resolve, reject }]);
      if (!timer) timer = setTimeout(flush, waitMs);
    });
};

/** fetch has no timeout of its own; a hung read would leave every card loading. */
const readSummariesWithDeadline = async (assignmentIds) => {
  const controller = new AbortController();
  let timer;
  const deadline = new Promise((_, reject) => {
    timer = setTimeout(() => {
      reject(
        Object.assign(new Error('Status read timed out. Please retry.'), {
          status: 408,
          name: 'TimeoutError',
        })
      );
      controller.abort();
    }, STATUS_READ_TIMEOUT_MS);
  });
  try {
    return await Promise.race([
      getAssignmentFeedbackSummaries(assignmentIds, {
        signal: controller.signal,
      }),
      deadline,
    ]);
  } finally {
    clearTimeout(timer);
  }
};

export const loadAssignmentFeedbackSummary = createBatchLoader(
  readSummariesWithDeadline
);

/**
 * What a card draws from a summary, by the same rules feedbackRun.js applies to the
 * rows: active while any row is PENDING or GENERATING, or while the server says its
 * bulk run is still going; progress = settled / total.
 */
export const summaryProgress = (summary) => {
  const total = summary?.total ?? 0;
  const settled =
    (summary?.feedbackReady ?? 0) + (summary?.feedbackFailed ?? 0);
  return {
    isActive:
      (summary?.feedbackPending ?? 0) + (summary?.feedbackGenerating ?? 0) >
        0 || isRunInProgress(summary),
    progress: total === 0 ? 0 : settled / total,
  };
};

/**
 * A bulk run marks its responses one at a time, and the one waiting its turn reads as
 * NOT_REQUESTED: between two responses no row is in flight. The server says when its
 * run is still going (StatusSummary.runInProgress), so the bar holds its percentage and
 * the poll keeps going through those stretches instead of stopping until a reload.
 */
const isRunInProgress = (summary) => summary?.runInProgress === true;

const rowsProgress = (status) => ({
  isActive: isRunActive(status) || isRunInProgress(status?.summary),
  progress: runProgress(status),
});

const IDLE = { isActive: false, progress: 0 };

/**
 * A backend without the summaries route answers 404, or 405 where the POST lands on
 * GET /assignments/{id}. Until it is deployed, a card reads its own status as before.
 */
export const isSummariesRouteMissing = (error) =>
  error?.status === 404 || error?.status === 405;

/**
 * `{ isActive, progress }` for one task card.
 *
 * The summary is the first read and, for a finished assignment, the only one. The
 * per-id status — shared with the wizard, the student switcher and the open document
 * — takes over while a run is moving: when the summary says so, when a feedback
 * request for this assignment is in flight, or when another screen's poll has left
 * this assignment's rows active in the cache. Its poll stops by itself once the run
 * settles, exactly as it did when every card read it.
 */
export const useTaskFeedbackProgress = (
  assignmentId,
  { enabled = true } = {}
) => {
  const client = useQueryClient();
  const canRead = Boolean(assignmentId) && enabled;
  const { data: summary, error: summaryError } = useQuery({
    queryKey: assignmentFeedbackSummaryKey(assignmentId),
    queryFn: () => loadAssignmentFeedbackSummary(assignmentId),
    enabled: canRead,
    networkMode: 'always',
    retry: shouldRetryQuery,
    // Unlike createGlobalState's refetchOnMount: false. Nothing else writes this key —
    // a run started elsewhere updates only the per-id status — so a card coming back
    // into view must re-read it. Remounting cards share one batched read, and so do
    // the re-reads when the connection comes back.
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  });
  const isRequesting =
    useIsMutating({ mutationKey: assignmentFeedbackRequestKey(assignmentId) }) >
    0;
  // Not reactive by itself, but this card observes the key below, so a write to it
  // re-renders the card and this is read again.
  const cachedRun = assignmentId
    ? client.getQueryData(assignmentFeedbackRunKey(assignmentId))
    : undefined;
  const isWatching =
    canRead &&
    (summaryProgress(summary).isActive ||
      isSummariesRouteMissing(summaryError) ||
      isRequesting ||
      rowsProgress(cachedRun).isActive);
  const {
    data: run,
    isError: isRowsError,
    errorUpdatedAt: rowsFailedAt,
    isFetchedAfterMount,
    refetch: readRows,
  } = useAssignmentSubmissionsStatus(assignmentId, {
    enabled: isWatching,
    // Rows alone stop the poll in a gap between two responses; the run does not.
    isRunRequested: isRequesting || isRunInProgress(cachedRun?.summary),
  });
  useStatusRecovery({
    isFailing: isWatching && isRowsError,
    failedAt: rowsFailedAt,
    readRows,
  });
  // Rows read since this card mounted are newer than the summary. Rows cached before
  // it are trusted only while they show a run moving; settled ones are re-read below.
  const hasLiveRows =
    Boolean(run) && (isFetchedAfterMount || rowsProgress(run).isActive);
  // The status query never refetches on mount, and settled rows schedule no poll, so
  // rows cached before this run began would otherwise never be read again.
  useEffect(() => {
    if (isWatching && run && !hasLiveRows) readRows().catch(() => {});
  }, [isWatching, run, hasLiveRows, readRows]);
  return useMemo(() => {
    // A failed status read stops the poll, so whatever was last drawn would never move
    // again: a bar frozen at its last percentage reads as a run still going. Show none
    // until a read succeeds. (Before batching, the card kept the frozen bar.)
    if (isWatching && isRowsError) return IDLE;
    if (hasLiveRows) return rowsProgress(run);
    return summaryProgress(summary);
  }, [hasLiveRows, run, isWatching, isRowsError, summary]);
};

/**
 * A failed status read stops the 2.5 s poll for good (runPollInterval), so a brief outage
 * would hide a live run until the page reloads. Try again a few times, slowly; a success
 * resumes the poll on its own if the run is still moving.
 */
const useStatusRecovery = ({ isFailing, failedAt, readRows }) => {
  const attempts = useRef(0);
  useEffect(() => {
    if (!isFailing) {
      attempts.current = 0;
      return undefined;
    }
    if (attempts.current >= STATUS_RECOVERY_ATTEMPTS) return undefined;
    const timer = setTimeout(() => {
      attempts.current += 1;
      readRows().catch(() => {});
    }, STATUS_RECOVERY_DELAY_MS);
    return () => clearTimeout(timer);
  }, [isFailing, failedAt, readRows]);
};
